import React, { useState, useEffect } from "react";
import { Box, Button } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { C, F, trans } from "../tokens";

const NAV = [
  { label: "Accueil",     to: "/" },
  { label: "À propos",    to: "/votre-osteopathe" },
  { label: "Pathologies", to: "/pathologies" },
  { label: "Cabinets",    to: "/cabinets" },
  { label: "Tarifs",       to: "/tarifs-et-remboursements" },
  { label: "FAQ",          to: "/faq" },
  { label: "Partenaires",  to: "/partenariats" },
];

const Header = () => {
  const [open, setOpen]         = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location                = useLocation();

  const toggle = () => setOpen((o) => !o);
  const close  = () => setOpen(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { close(); }, [location]);

  return (
    <>
      {open && (
        <Box
          onClick={close}
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.45)",
            zIndex: 998,
            display: { xs: "block", md: "none" },
          }}
        />
      )}

      <Box
        component="nav"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "fixed",
          width: "100%",
          height: "68px",
          px: { xs: "1.25rem", md: "2.5rem" },
          backgroundColor: scrolled ? "rgba(17,29,20,.96)" : C.dark,
          backdropFilter: scrolled ? "blur(12px)" : "none",
          color: "#fff",
          zIndex: 1000,
          transition: "background-color .3s ease, backdrop-filter .3s ease",
        }}
      >
        {/* Logo */}
        <Box sx={{ display: "flex", alignItems: "center", gap: ".6rem", cursor: "default" }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: C.primary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: F.display,
              fontStyle: "italic",
              fontSize: "1rem",
              color: "#fff",
              flexShrink: 0,
            }}
          >
            M
          </Box>
          <Box
            component="span"
            sx={{ fontFamily: F.body, fontSize: "1rem", fontWeight: 500, color: "#fff", whiteSpace: "nowrap" }}
          >
            Maxime Nicolaieff
          </Box>
        </Box>

        {/* Nav links */}
        <Box
          component="ul"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: ".15rem",
            "@media (max-width: 900px)": {
              flexDirection: "column",
              justifyContent: "center",
              position: "fixed",
              top: 0,
              right: open ? 0 : "-100vw",
              width: open ? "min(80vw, 320px)" : 0,
              height: "100vh",
              padding: "2rem 1.5rem",
              backgroundColor: C.dark,
              zIndex: 999,
              visibility: open ? "visible" : "hidden",
              transition: "right .4s ease-out, width .4s ease-out, visibility .4s",
              gap: 0,
              overflow: "hidden",
            },
          }}
        >
          {NAV.map((item, i) => (
            <Box
              component="li"
              key={item.to}
              sx={{
                "@media (max-width: 900px)": {
                  transform: open ? "translateX(0)" : "translateX(40px)",
                  opacity: open ? 1 : 0,
                  width: "100%",
                  transition: open ? `all .3s ease-out ${0.04 * (i + 1)}s` : "none",
                },
              }}
            >
              <Box
                component={Link}
                to={item.to}
                onClick={close}
                sx={{
                  display: "block",
                  padding: ".42rem .82rem",
                  color: location.pathname === item.to ? "#fff" : "rgba(255,255,255,.78)",
                  fontSize: ".82rem",
                  fontWeight: location.pathname === item.to ? 500 : 400,
                  borderRadius: "6px",
                  transition: `all ${trans}`,
                  whiteSpace: "nowrap",
                  textDecoration: "none",
                  "&:hover": { color: "#fff", background: "rgba(255,255,255,.1)" },
                  "@media (max-width: 900px)": {
                    padding: "1rem 1rem",
                    fontSize: ".95rem",
                    color: "rgba(255,255,255,.88)",
                    borderBottom: "1px solid rgba(255,255,255,.08)",
                    borderRadius: 0,
                  },
                }}
              >
                {item.label}
              </Box>
            </Box>
          ))}

          <Box
            component="li"
            sx={{
              "@media (max-width: 900px)": {
                transform: open ? "translateX(0)" : "translateX(40px)",
                opacity: open ? 1 : 0,
                width: "100%",
                transition: open ? `all .3s ease-out ${0.04 * (NAV.length + 1)}s` : "none",
              },
            }}
          >
            <Box
              component="a"
              href="https://www.doctolib.fr/osteopathe/balma/maxime-nicolaieff"
              target="_blank"
              rel="noreferrer"
              onClick={close}
              sx={{
                display: "block",
                ml: { md: ".5rem" },
                padding: ".5rem 1.2rem",
                background: C.accent,
                color: "#fff",
                borderRadius: "50px",
                fontSize: ".82rem",
                fontWeight: 500,
                transition: `all ${trans}`,
                textDecoration: "none",
                "&:hover": { background: "#b05540", transform: "translateY(-1px)", color: "#fff" },
                "@media (max-width: 900px)": {
                  ml: 0,
                  mt: "1rem",
                  textAlign: "center",
                  width: "100%",
                  borderRadius: "50px",
                },
              }}
            >
              Prendre RDV
            </Box>
          </Box>
        </Box>

        {/* Burger */}
        <Button
          onClick={toggle}
          aria-label="Menu"
          disableRipple
          sx={{
            width: 40,
            height: 40,
            minWidth: "unset",
            background: "transparent",
            border: "none",
            color: "#fff",
            cursor: "pointer",
            display: { xs: "flex", md: "none" },
            position: "fixed",
            top: "1rem",
            right: "1rem",
            zIndex: 1001,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="span"
            sx={{
              display: "block",
              width: open ? 0 : "22px",
              height: "2px",
              borderRadius: "2px",
              background: open ? "transparent" : "#fff",
              position: "relative",
              transition: "all .35s ease-in-out",
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                display: "block",
                width: "22px",
                height: "2px",
                borderRadius: "2px",
                background: "#fff",
                transform: open ? "rotate(45deg)" : "translateY(-7px)",
                transition: "all .35s ease-in-out",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                left: 0,
                display: "block",
                width: "22px",
                height: "2px",
                borderRadius: "2px",
                background: "#fff",
                transform: open ? "rotate(-45deg)" : "translateY(7px)",
                transition: "all .35s ease-in-out",
              },
            }}
          />
        </Button>
      </Box>
    </>
  );
};

export default Header;
